import { useState, useMemo } from "react";

export type ExtraFrequency = "weekly" | "fortnightly" | "monthly" | "yearly";

export interface ExtraRepaymentInputs {
  loanAmount: number;
  interestRate: number;
  loanTerm: number;
  extraAmount: number;
  extraFrequency: ExtraFrequency;
  lumpSum: number;
  lumpSumYear: number;
}

export interface ExtraRepaymentResults {
  // Without extra payments
  originalMonthlyPayment: number;
  originalTotalInterest: number;
  originalTotalRepayments: number;
  originalTermMonths: number;

  // With extra payments
  newMonthlyPayment: number;
  newTotalInterest: number;
  newTotalRepayments: number;
  newTermMonths: number;

  // Savings
  interestSaved: number;
  timeSavedMonths: number;
  effectiveExtraMonthly: number;
}

const defaultInputs: ExtraRepaymentInputs = {
  loanAmount: 500000,
  interestRate: 6.0,
  loanTerm: 30,
  extraAmount: 200,
  extraFrequency: "monthly",
  lumpSum: 0,
  lumpSumYear: 1,
};

/**
 * Calculate monthly repayment using PMT formula
 */
function calculateMonthlyPayment(principal: number, annualRate: number, years: number): number {
  const monthlyRate = annualRate / 100 / 12;
  const periods = years * 12;

  if (monthlyRate === 0) return principal / periods;

  return (
    (principal * (monthlyRate * Math.pow(1 + monthlyRate, periods))) /
    (Math.pow(1 + monthlyRate, periods) - 1)
  );
}

/**
 * Calculate total interest and term with extra payments
 */
function calculateWithExtraPayments(
  principal: number,
  annualRate: number,
  baseMonthlyPayment: number,
  extraMonthly: number,
  lumpSum: number,
  lumpSumMonth: number
): { totalInterest: number; termMonths: number; totalRepayments: number } {
  const monthlyRate = annualRate / 100 / 12;
  let balance = principal;
  let totalInterest = 0;
  let month = 0;
  const maxMonths = 30 * 12; // Safety limit

  while (balance > 0.01 && month < maxMonths) {
    month++;

    // Apply lump sum if applicable
    if (lumpSum > 0 && month === lumpSumMonth) {
      balance = Math.max(0, balance - lumpSum);
    }

    // Calculate interest for this month
    const interestPayment = balance * monthlyRate;
    totalInterest += interestPayment;

    // Calculate total payment (base + extra)
    const totalPayment = Math.min(baseMonthlyPayment + extraMonthly, balance + interestPayment);

    // Calculate principal payment
    const principalPayment = totalPayment - interestPayment;

    // Reduce balance
    balance = Math.max(0, balance - principalPayment);
  }

  return {
    totalInterest,
    termMonths: month,
    totalRepayments: principal + totalInterest,
  };
}

/**
 * Convert extra payment frequency to monthly equivalent
 */
function getMonthlyEquivalent(amount: number, frequency: ExtraFrequency): number {
  switch (frequency) {
    case "weekly":
      return (amount * 52) / 12;
    case "fortnightly":
      return (amount * 26) / 12;
    case "monthly":
      return amount;
    case "yearly":
      return amount / 12;
    default:
      return amount;
  }
}

/**
 * Extra Repayment Calculator Hook
 */
export function useExtraRepaymentCalculator() {
  const [inputs, setInputs] = useState<ExtraRepaymentInputs>(defaultInputs);

  const results = useMemo<ExtraRepaymentResults>(() => {
    const { loanAmount, interestRate, loanTerm, extraAmount, extraFrequency, lumpSum, lumpSumYear } =
      inputs;

    // Calculate base monthly payment
    const originalMonthlyPayment = calculateMonthlyPayment(loanAmount, interestRate, loanTerm);

    // Calculate original loan (no extra payments)
    const original = calculateWithExtraPayments(
      loanAmount,
      interestRate,
      originalMonthlyPayment,
      0,
      0,
      0
    );

    // Calculate with extra payments
    const extraMonthly = getMonthlyEquivalent(extraAmount, extraFrequency);
    const lumpSumMonth = lumpSumYear * 12;

    const withExtra = calculateWithExtraPayments(
      loanAmount,
      interestRate,
      originalMonthlyPayment,
      extraMonthly,
      lumpSum,
      lumpSumMonth
    );

    return {
      // Original
      originalMonthlyPayment: Math.round(originalMonthlyPayment * 100) / 100,
      originalTotalInterest: Math.round(original.totalInterest * 100) / 100,
      originalTotalRepayments: Math.round(original.totalRepayments * 100) / 100,
      originalTermMonths: original.termMonths,

      // With extra
      newMonthlyPayment: Math.round(originalMonthlyPayment * 100) / 100,
      newTotalInterest: Math.round(withExtra.totalInterest * 100) / 100,
      newTotalRepayments: Math.round(withExtra.totalRepayments * 100) / 100,
      newTermMonths: withExtra.termMonths,

      // Savings
      interestSaved: Math.round((original.totalInterest - withExtra.totalInterest) * 100) / 100,
      timeSavedMonths: original.termMonths - withExtra.termMonths,
      effectiveExtraMonthly: Math.round(extraMonthly * 100) / 100,
    };
  }, [inputs]);

  const updateInput = <K extends keyof ExtraRepaymentInputs>(
    key: K,
    value: ExtraRepaymentInputs[K]
  ) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
  };

  return { inputs, results, updateInput, setInputs };
}
